import clsx from "clsx";
import { forwardRef, useRef, useState } from "react";

import { useSavedAddresses } from "@web-app/api";
import { SavedAddress } from "@web-app/types";

import { Icon } from "../Icon";
import { Field } from "../Input";
import { Label } from "../Label";

interface InputWithAddressAutoCompleteProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange"
> {
  currentWorkspaceId: string;
  invalid?: boolean;
  label?: string;
  onAddressSelect?: (address: SavedAddress) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClickAddress: (address: SavedAddress) => void;
  toolTipText?: string;
}

export const InputWithAddressAutoComplete = forwardRef<
  HTMLInputElement,
  InputWithAddressAutoCompleteProps
>(
  (
    {
      className,
      invalid,
      label,
      required,
      toolTipText,
      currentWorkspaceId,
      onClickAddress,
      onChange,
      disabled,
      onAddressSelect,
      ...props
    },
    ref,
  ) => {
    const [value, setValue] = useState(props.defaultValue?.toString() || "");
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const { data: savedAddressesList, isFetching } =
      useSavedAddresses(currentWorkspaceId);

    const filteredAddresses = savedAddressesList?.filter(
      (address: SavedAddress) =>
        [address.address, address.name].some((text) =>
          text.toLowerCase().includes(value.toLowerCase()),
        ),
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      onChange?.(e);
    };

    const handleAddressSelect = (address: SavedAddress) => {
      onClickAddress(address);
      onAddressSelect?.(address);
      inputRef.current?.focus();
      setShowDropdown(false);
    };

    return (
      <Field className="relative">
        {label && (
          <Label
            required={required}
            toolTipText={toolTipText}
            disabled={disabled}
          >
            {label}
          </Label>
        )}

        <div className="relative">
          <input
            {...props}
            ref={(node) => {
              // Handle both forwardRef and local ref
              if (typeof ref === "function") {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
              inputRef.current = node;
            }}
            className={clsx(
              "block w-full rounded-md border px-3 py-2 pr-10 text-gray-900 shadow-sm text-xs sm:text-sm",
              invalid
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
              "focus:outline-none focus:ring-2 focus:ring-opacity-50",
              "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
              className,
            )}
            onChange={handleChange}
            autoComplete="off"
            disabled={disabled}
          />
          <button
            type="button"
            onClick={() => {
              setShowDropdown(!showDropdown);
              inputRef.current?.focus();
            }}
            disabled={disabled}
            className={clsx(
              "absolute right-2 top-1/2 -translate-y-1/2 bg-transparent ",
              "rounded-md p-1.5 transition-colors duration-150",
              "text-gray-400 hover:text-gray-600",
              "hover:bg-gray-100/80",
              "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
              "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-400",
              showDropdown && "bg-gray-100 text-gray-600",
            )}
            aria-label="Show saved addresses"
          >
            <Icon name="addressBook" className="h-3 md:h-4 w-3 md:w-4" />
          </button>
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
            {isFetching ? (
              <div className="flex items-center space-x-2 p-3 text-xs md:text-sm text-gray-600 bg-white">
                <Icon
                  name="circle"
                  className="h-3 md:h-4 w-3 md:w-4 animate-spin"
                />
                <span>Fetching addresses...</span>
              </div>
            ) : filteredAddresses?.length ? (
              <div className="max-h-[330px] divide-y overflow-y-auto bg-white">
                {filteredAddresses.map((addr: SavedAddress) => (
                  <button
                    key={addr.id}
                    type="button"
                    onClick={() => handleAddressSelect(addr)}
                    className={clsx(
                      "w-full p-3 text-left transition-colors duration-150 bg-white",
                      "hover:bg-gray-50 focus:bg-gray-50 focus:outline-none",
                      "focus:ring-2 focus:ring-inset focus:ring-blue-500",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        name="addressBook"
                        className="h-3 md:h-4 w-3 md:w-4 text-gray-400"
                      />
                      <div className="font-medium text-xs md:text-sm text-gray-900">
                        {addr.name}
                      </div>
                    </div>
                    <div className="mt-1 truncate text-[10px] md:text-sm text-gray-500">
                      {addr.address}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-3 text-xs md:text-sm text-gray-600 bg-white">
                No saved addresses
              </div>
            )}
          </div>
        )}
      </Field>
    );
  },
);

InputWithAddressAutoComplete.displayName = "InputWithAddressAutoComplete";
